import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { InvitationAction, useInvitationHandler } from "../../hooks/invitations";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_INVITATION } from "../../graphql/invitation";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { useI18n } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { ScrollView, StyleSheet, View } from "react-native";
import Button from "../../components/Button";
import VStack from "../../ui_atoms/VStack";
import { useSnackbar } from "../../hooks/snackbar";
import ListItem from "../../ui_atoms/ListItem";
import HStack from "../../ui_atoms/HStack";
import { useTheme } from "../../hooks/use-theme";
import Gap from "../../ui_atoms/Gap";
import BackgroundImage from "../../assets/images/invitation_image.svg";

type WorkerInvitationDetailsProps = StackScreenProps<RootStackParamList, "WorkerInvitationDetails">;

const WorkerInvitationDetailsScreen = ({ route, navigation }: WorkerInvitationDetailsProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const invitationId = route?.params?.invitationId || "";
    const { refreshUser } = useContext(AuthManagerContext);
    const {
        data,
        error: getError,
        loading: getLoading,
    } = useQuery(GET_INVITATION, {
        variables: { invitationId: invitationId },
    });
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        if (!data?.getInvitation && !getLoading) {
            navigation.replace("Home", {
                screen: "MissionsScreen",
                params: { screen: "MissionList" },
            });
            showSnackbar({ text: i18n.t("invitation.invitation_not_found") });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, getLoading]);

    const onActionSuccess = useCallback(
        (action: InvitationAction) => {
            if (["accept", "reject", "cancel", "resend"].includes(action)) {
                void refreshUser?.();
                showSnackbar({
                    text: i18n.t(`invitation.action_result.${action}.success`),
                    mode: "success",
                });
            }
            navigation.popToTop();
        },
        [navigation, refreshUser, showSnackbar, i18n],
    );

    const {
        onAction,
        processingAction,
        error: procError,
        reset,
    } = useInvitationHandler(onActionSuccess);

    const invitation = data?.getInvitation || null;

    const apolloError = procError || getError || null;
    const error = apolloError
        ? i18n.t(`invitation.action_result.${processingAction || "default"}.error`)
        : null;

    return (
        <>
            <LoadingBoundary
                loading={getLoading && !data}
                error={error ? i18n.t("invitation.send.error") : undefined}
                clearError={reset}
            >
                {invitation && (
                    <ScrollView>
                        <VStack gap={1} alignment="stretch">
                            <Gap gap={1} />
                            <View style={{ flex: 1, width: "100%" }}>
                                <BackgroundImage width="100%" color={colors.body} />
                            </View>
                            <ListItem
                                useBigAvatarOrIcon
                                inGroup
                                avatarPath={invitation.clientLogoUrl}
                                title={invitation.clientName}
                                description={i18n.t("invitation.description", {
                                    senderName: invitation.senderName,
                                })}
                            />
                            <HStack h="2/3">
                                <Button
                                    size="large"
                                    buttonMode="outlined"
                                    onPress={async () => await onAction("reject", invitation)}
                                    label={i18n.t(`invitation.action.reject`)}
                                    style={styles.rejectButton}
                                />
                                <Gap gap={1} />
                                <Button
                                    size="large"
                                    buttonMode="secondary"
                                    onPress={async () => await onAction("accept", invitation)}
                                    label={i18n.t(`invitation.action.accept`)}
                                    style={styles.acceptButton}
                                />
                            </HStack>
                        </VStack>
                    </ScrollView>
                )}
            </LoadingBoundary>
        </>
    );
};

const useStyles = () => {
    const { colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                rejectButton: {
                    flex: 3,
                    backgroundColor: colors.accentLighter,
                    borderWidth: 0,
                },
                acceptButton: {
                    flex: 5,
                },
            }),
        [colors.accentLighter],
    );
};

export default WorkerInvitationDetailsScreen;
